/**
 * Neutral list API contract (Kendo-free).
 *
 * Use this everywhere in the React app. Adapters map to/from the current
 * Kendo DynamicLinq shape while the old API exists. When you move to .NET Core,
 * return { data, total } and accept this body — no Kendo NuGet needed.
 *
 * --- Future .NET Core endpoint (example) ---
 * POST /api/locations/list
 * Body: ListQuery { skip, take, sort[], filter? }
 * Returns: { data: T[], total: number }
 *
 * Implement with EF Core: Where + OrderBy + CountAsync + Skip/Take.
 * Do NOT use Kendo.DynamicLinq / ToDataSourceResult.
 */

/** @typedef {'asc'|'desc'} SortDir */

/**
 * @typedef {Object} SortDescriptor
 * @property {string} field
 * @property {SortDir} dir
 */

/**
 * @typedef {Object} FilterDescriptor
 * @property {string} [field]
 * @property {string} [operator]
 * @property {*} [value]
 * @property {'and'|'or'} [logic]
 * @property {FilterDescriptor[]} [filters]
 */

/**
 * @typedef {Object} ListQuery
 * @property {number} skip
 * @property {number} take
 * @property {SortDescriptor[]} sort
 * @property {FilterDescriptor|null} filter
 */

/** Transport used when calling the current API. Flip to 'neutral' after .NET Core cutover. */
export const GRID_TRANSPORT = {
  /** Current ASP.NET + Kendo.DynamicLinq */
  KENDO: 'kendo',
  /** Future .NET Core: same ListQuery JSON, response { data, total } */
  NEUTRAL: 'neutral',
};

/** Default while old API is live. Change to GRID_TRANSPORT.NEUTRAL after migration. */
export const ACTIVE_GRID_TRANSPORT = GRID_TRANSPORT.KENDO;

/**
 * Masters stay client-side (load all, filter in browser).
 * Large grids (orders, etc.) should use 'server' later.
 */
export const GRID_MODE = {
  CLIENT: 'client',
  SERVER: 'server',
};

export const createListQuery = ({
  skip = 0,
  take = 50,
  sort = [],
  filter = null,
} = {}) => ({
  skip: Number(skip) || 0,
  take: Math.max(1, Number(take) || 50),
  sort: Array.isArray(sort) ? sort : [],
  filter: filter || null,
});

/**
 * Build ListQuery from column-menu UI state (for future server-side masters / large grids).
 * @param {Object} opts
 * @param {number} [opts.skip]
 * @param {number} [opts.take]
 * @param {string} [opts.sortField]
 * @param {'asc'|'desc'} [opts.sortDirection]
 * @param {Record<string, {logic:string, filters: Array<{operator:string, value:*}>>}} [opts.filtersByField]
 * @param {Record<string, string>} [opts.fieldMap] UI field → API field (e.g. name → Name)
 */
export const buildListQueryFromColumnMenu = ({
  skip = 0,
  take = 50,
  sortField = '',
  sortDirection = 'asc',
  filtersByField = {},
  fieldMap = {},
} = {}) => {
  const mapField = (field) => fieldMap[field] || field;

  const sort = sortField
    ? [{ field: mapField(sortField), dir: sortDirection === 'desc' ? 'desc' : 'asc' }]
    : [];

  const columnFilters = Object.entries(filtersByField || {})
    .map(([field, colFilter]) => {
      if (!colFilter?.filters?.length) return null;
      const active = colFilter.filters
        .filter((f) => {
          if (f.operator === 'isempty' || f.operator === 'isnotempty') return true;
          return String(f.value ?? '').trim() !== '';
        })
        .map((f) => ({
          field: mapField(field),
          operator: f.operator,
          value: f.value,
        }));
      if (!active.length) return null;
      if (active.length === 1) return active[0];
      return {
        logic: colFilter.logic === 'or' ? 'or' : 'and',
        filters: active,
      };
    })
    .filter(Boolean);

  let filter = null;
  if (columnFilters.length === 1) {
    filter = columnFilters[0];
  } else if (columnFilters.length > 1) {
    filter = { logic: 'and', filters: columnFilters };
  }

  return createListQuery({ skip, take, sort, filter });
};
